import { useEffect, useState } from "react";
import { Container, Graphics, Sprite } from "@pixi/react";
import { ColorOverlayFilter } from "@pixi/filter-color-overlay";
import { ParkPlaceRect, PlaceCircle } from "./SpriteIndex";
import BoundingBox from "../../../Domain/Types/BoundingBox.type";
import { ToolSelection } from "../../Views/CreateFloorPlanView/CreateFloorPlanView";
import { PlaceVariant } from "../../../Domain/Types/FloorPlan/PlaceVariant.type";
import { variantCircle } from "./PlaceSprite.functions";
import { drawPlaceColor } from "../functions/drawPlaceColor.function";
import { FavoriteStarGraphic, FAVORITE_STAR_OUTER_BASE } from "../../FavoriteStarGraphic";

interface Props {
  placeTypeId: number;
  boundingBox: BoundingBox;
  filterGroup: ColorOverlayFilter[];
  variantColor: number;
  variant: PlaceVariant;
  placeScale: number;
  isSelectable: boolean;
  isHighlighted?: boolean;
  isFavorite?: boolean;
  colorOverlay: number | undefined;
  tool?: ToolSelection;
  icon?: string;
  isColleaguesPlace?: boolean;
  isGrpPreferred: boolean;
}

/**
 * draw the place sprite, shared for the parking place and electric charging station
 * by pixi-sprite or pixi-graphic according to module(booking/creating)
 * @version 1.0.0
 */
export function ParkPlaceSprite({
  placeTypeId,
  boundingBox,
  filterGroup,
  variantColor,
  variant,
  placeScale,
  isSelectable,
  isHighlighted,
  isFavorite = false,
  colorOverlay,
  tool,
  isColleaguesPlace,
  isGrpPreferred
}: Readonly<Props>) {
  const { width = 160, height = 80 } = boundingBox;

  const favoriteStarScale = Math.min(0.85, Math.max(0.38, placeScale * 0.12));
  const wS = width * placeScale;
  const hS = height * placeScale;
  const starOuter = FAVORITE_STAR_OUTER_BASE * favoriteStarScale;
  const inset = 5 * placeScale;
  const favoriteStarPosition = {
    x: wS / 2 - inset - starOuter / 2,
    y: -hS / 2 + inset + starOuter / 2
  };

  const [circleRadius, setCircleRadius] = useState(width * placeScale * 0.2);
  const [lineWidth, setLineWidth] = useState(width * placeScale * 0.02);

  const circleSize = (width * placeScale) / 2.1;
  const circlePos = 0 - circleSize / 2;

  const { rectColor, circColor } = drawPlaceColor({
    isHighlighted: isHighlighted ?? false,
    colorOverlay,
    variantColor,
    tool
  });

  useEffect(() => {
    setCircleRadius(width * placeScale * 0.2);
    setLineWidth(width * placeScale * 0.02);
  }, [width, height, placeScale]);

  /** draw parkplace sprite by SVG Image when is not graphic repreferred(firefox) and booking mode */
  const isDrawSprSvg = !isGrpPreferred && tool === "SELECT.BOOK";
  /** draw parkplace sprite by PIXI.Graphics when graphic repreferred(firefox) and not booking mode */
  const isDrawSprGrp = isGrpPreferred || tool !== "SELECT.BOOK";

  return (
    <>
      {isDrawSprSvg && (
        <ParkPlaceSpriteSvgImg
          isSelectable={isSelectable}
          width={width}
          height={height}
          placeTypeId={placeTypeId}
          placeScale={placeScale}
          filterGroup={filterGroup}
          circleSize={circleSize}
          circlePos={circlePos}
          variant={variant}
          isColleaguesPlace={isColleaguesPlace}
        />
      )}

      {isDrawSprGrp && (
        <ParkPlaceSpriteGraphic
          isSelectable={isSelectable}
          width={width}
          height={height}
          placeTypeId={placeTypeId}
          placeScale={placeScale}
          filterGroup={filterGroup}
          rectColor={rectColor}
          lineWidth={lineWidth}
          circColor={circColor}
          circleRadius={circleRadius}
          isColleaguesPlace={isColleaguesPlace}
        />
      )}
      {isFavorite && (
        <Container position={favoriteStarPosition}>
          <FavoriteStarGraphic starScale={favoriteStarScale} />
        </Container>
      )}
    </>
  );
}

export function borderColorPerType(placeTypeId: number) {
  const parkingPlaceBorder = 0x0000ff;
  const electricChargingBorder = 0x75baff;

  switch (placeTypeId) {
    case 2:
      return parkingPlaceBorder;
    case 4:
      return electricChargingBorder;
    default:
      return parkingPlaceBorder;
  }
}
export function spriteBorderColorPerType(placeTypeId: number) {
  switch (placeTypeId) {
    case 2:
      return ParkPlaceRect.Parking;
    case 4:
      return ParkPlaceRect.ElectricCharging;
    default:
      return ParkPlaceRect.Parking;
  }
}

/** draw parkplace sprite by SVG Image when is not graphic repreferred(firefox) and booking mode */
export function ParkPlaceSpriteSvgImg({
  isSelectable,
  width,
  height,
  placeTypeId,
  placeScale,
  filterGroup,
  circleSize,
  circlePos,
  variant,
  isColleaguesPlace
}: Readonly<{
  isSelectable: boolean;
  width: number;
  height: number;
  placeTypeId: number;
  placeScale: number;
  filterGroup: ColorOverlayFilter[];
  circleSize: number;
  circlePos: number;
  variant: PlaceVariant;
  isColleaguesPlace?: boolean;
}>) {
  return (
    <>
      {/* Sprite Object, rectangle of parking places */}
      <Sprite
        data-testid="parkingplace-spr-rect"
        image={spriteBorderColorPerType(placeTypeId)}
        position={{ x: 0 - (width * placeScale) / 2, y: 0 - (height * placeScale) / 2 }}
        width={width * placeScale}
        height={height * placeScale}
        filters={filterGroup}
        cursor={isSelectable ? "pointer" : "auto"}
      />
      {/* Sprite Object, circle of parking places */}
      {isColleaguesPlace ? (
        <Sprite
          data-testid="parkingplace-colleagues-spr-circ"
          image={PlaceCircle.Occupied}
          width={circleSize * 2.2}
          height={circleSize * 2.2}
          position={{ x: circlePos * 2.2, y: circlePos * 2.2 - (height * placeScale) / 4 }}
          filters={filterGroup}
          cursor={isSelectable ? "pointer" : "auto"}
        />
      ) : (
        <Sprite
          data-testid="parkingplace-spr-circ"
          image={variantCircle(variant)}
          width={circleSize}
          height={circleSize}
          position={{ x: circlePos, y: circlePos - (height * placeScale) / 4 }}
          filters={filterGroup}
          cursor={isSelectable ? "pointer" : "auto"}
        />
      )}
    </>
  );
}

/** draw parkplace sprite by PIXI.Graphics when graphic repreferred(firefox) and not booking mode */
export function ParkPlaceSpriteGraphic({
  isSelectable,
  width,
  height,
  placeTypeId,
  placeScale,
  filterGroup,
  rectColor,
  lineWidth,
  circColor,
  circleRadius,
  isColleaguesPlace
}: Readonly<{
  isSelectable: boolean;
  width: number;
  height: number;
  placeScale: number;
  placeTypeId: number;
  filterGroup: ColorOverlayFilter[];
  rectColor: number;
  lineWidth: number;
  circColor: number;
  circleRadius: number;
  isColleaguesPlace?: boolean;
}>) {
  return (
    <>
      {/* Graphic objects, rectangle of parking places */}
      <Graphics
        data-testid="parkingplace-grp-rect"
        cursor={isSelectable ? "pointer" : "auto"}
        filters={filterGroup}
        position={{ x: 0 - (width * placeScale) / 2, y: 0 - (height * placeScale) / 2 }}
        draw={g => {
          g.clear();
          g.lineStyle(5 * placeScale, borderColorPerType(placeTypeId));
          /** g.beginFill(0xd2dae1, 1); */
          g.beginFill(rectColor, 1);
          g.drawRect(0, 0, width * placeScale, height * placeScale);
          g.endFill();
        }}
      />
      {/* Graphic objects, circle of parking places */}
      {isColleaguesPlace ? (
        <Graphics
          data-testid="workplace-grp-circ"
          cursor={isSelectable ? "pointer" : "auto"}
          position={{ x: 0, y: 0 - (height * placeScale) / 4 }}
          filters={filterGroup}
          draw={g => {
            g.clear();
            g.lineStyle(lineWidth, 0xffffff); // prev: 6 * placeScale
            g.beginFill(0xff0000, 1);
            g.drawCircle(0, 0, circleRadius * 2.2); // prev : 30 * placeScale
            g.endFill();
          }}
        />
      ) : (
        <Graphics
          data-testid="parkingplace-grp-circ"
          cursor={isSelectable ? "pointer" : "auto"}
          filters={filterGroup}
          position={{ x: 0, y: 0 - (height * placeScale) / 4 }}
          draw={g => {
            g.clear();
            g.lineStyle(lineWidth, 0xffffff);
            /** g.beginFill(variantColor, 1); */
            g.beginFill(circColor, 1);
            g.drawCircle(0, 0, circleRadius);
            g.endFill();
          }}
        />
      )}
    </>
  );
}
